/**
 * Lazy MediaPipe BlazeFace (short range) loader — SPEC_V2 §7.
 * Model + WASM are self-hosted under /mediapipe (see scripts/vendor-mediapipe.mjs).
 * Detection only: we ask "is there a face?" and nothing else.
 */
export interface FaceDetectorLike {
  /** True when at least one face is detected in the current frame. */
  hasFace(frame: HTMLVideoElement, timestampMs: number): boolean;
  /**
   * v9 get-ready (lighting and framing): the most confident face's box in
   * frame-relative units (0..1), or null. Optional for fakes.
   */
  locate?(frame: HTMLVideoElement, timestampMs: number): FaceBox | null;
}

/** A face box relative to the frame (0..1 on both axes). */
export interface FaceBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

export type DetectorLoader = () => Promise<FaceDetectorLike>;

const BASE = `${import.meta.env.BASE_URL ?? '/'}mediapipe`;

let shared: Promise<FaceDetectorLike> | null = null;

export const loadFaceDetector: DetectorLoader = () => {
  if (!shared) {
    shared = (async () => {
      const { FaceDetector, FilesetResolver } = await import('@mediapipe/tasks-vision');
      const fileset = await FilesetResolver.forVisionTasks(`${BASE}/wasm`);
      const det = await FaceDetector.createFromOptions(fileset, {
        baseOptions: { modelAssetPath: `${BASE}/blaze_face_short_range.tflite`, delegate: 'CPU' },
        runningMode: 'VIDEO',
        minDetectionConfidence: 0.7, // real faces score ~0.9; patterns/ceilings stay well below
      });
      let last = 0;
      // VIDEO mode needs strictly increasing timestamps across every caller.
      const detect = (frame: HTMLVideoElement, ts: number) => {
        const t = Math.max(Math.round(ts), last + 1);
        last = t;
        return det.detectForVideo(frame, t).detections;
      };
      return {
        hasFace(frame: HTMLVideoElement, ts: number) {
          return detect(frame, ts).length > 0;
        },
        locate(frame: HTMLVideoElement, ts: number) {
          const b = detect(frame, ts)[0]?.boundingBox;
          const W = frame.videoWidth || 1;
          const H = frame.videoHeight || 1;
          return b ? { x: b.originX / W, y: b.originY / H, w: b.width / W, h: b.height / H } : null;
        },
      };
    })().catch((e: unknown) => {
      shared = null;
      throw e;
    });
  }
  return shared;
};

/** A face detector for still frames (incoming video, SPEC_V4 §3). */
export interface FrameFaceDetectorLike {
  hasFace(frame: HTMLCanvasElement | HTMLVideoElement): boolean;
}
export type FrameDetectorLoader = () => Promise<FrameFaceDetectorLike>;

let sharedFrames: Promise<FrameFaceDetectorLike> | null = null;

/**
 * Separate IMAGE-mode instance for other people's video, so it never shares
 * VIDEO-mode timestamps/state with your own face check. Same self-hosted model.
 */
export const loadFrameFaceDetector: FrameDetectorLoader = () => {
  if (!sharedFrames) {
    sharedFrames = (async () => {
      const { FaceDetector, FilesetResolver } = await import('@mediapipe/tasks-vision');
      const fileset = await FilesetResolver.forVisionTasks(`${BASE}/wasm`);
      const det = await FaceDetector.createFromOptions(fileset, {
        baseOptions: { modelAssetPath: `${BASE}/blaze_face_short_range.tflite`, delegate: 'CPU' },
        runningMode: 'IMAGE',
        minDetectionConfidence: 0.5, // lenient: a missed face here could end someone else's call
      });
      return { hasFace: (frame: HTMLCanvasElement | HTMLVideoElement) => det.detect(frame).detections.length > 0 };
    })().catch((e: unknown) => {
      sharedFrames = null;
      throw e;
    });
  }
  return sharedFrames;
};

/** Warm the detector early (e.g. on Home) so the gate is instant. Errors are ignored here. */
export function preloadFaceDetector(): void {
  void loadFaceDetector().catch(() => undefined);
}
